/**
 * The element tally behind the end-of-session results screen.
 *
 * Extracted from ResultsScreen because the interesting rule is which rounds
 * COUNT, and that rule was wrong in a way no one could test while it lived
 * inline in a component that needs a full email fixture to render.
 *
 * THE RULE: a round that judged no elements is excluded from the tally, not
 * counted as zero.
 *
 * A legitimate email ends the round at the gate (Expert scenarios A and D) —
 * the learner calls the whole message and there is nothing to hunt, so the
 * server scores the call alone. PlayerShell records that round from an empty
 * answer map, which makes `correctCount` 0 against a `totalCount` of every
 * visible element. Summing that into the session percentage told a learner who
 * had correctly identified a legitimate email that they got five things wrong
 * (QA 2026-09-18).
 *
 * The per-round screen (ChallengeResults) was fixed for this on 2026-09-10; the
 * session screen was not, because it has no `gateOnly` prop at all. One helper
 * now, so the two screens cannot disagree again.
 */
import type { SafetyStatus } from '@/app/phishtrainer/play/_player/data/phishingChallenges'

/**
 * How many element judgements the overall Safe/Unsafe call is worth.
 *
 * Product decision, 2026-09-23 (item 15): the call is the decision this product
 * exists to train, so it weighs three flags, and a wrong one fails its round on
 * the server. THIS IS THE SERVER'S NUMBER — `INBOX_CALL_WEIGHT` in
 * apps/api/src/modules/phishtrainer/rounds/phishtrainer-rounds.lib.ts, which
 * scores the stored round, the admin report and the Library with it. The two
 * apps cannot share code, so both are held to the same worked examples
 * (__tests__/judgement-model-parity.spec.ts reads the API's fixture and its
 * source): one model, not a second one that exists only in the browser.
 */
export const INBOX_CALL_WEIGHT = 3

export interface SessionRoundEntry {
  correctCount: number
  totalCount: number
  /** The round ended at the Expert gate — no elements were judged. */
  gateOnly?: boolean
  /**
   * The Safe/Unsafe call the learner made at the Expert gate — recorded on every
   * Expert round whose gate the server graded, gate-only and hunt alike.
   *
   * A FACT ABOUT THE LEARNER, not a verdict. Use it for the "Called Safe" /
   * "Called Unsafe" label; decide whether they were right with gradedCallVerdict.
   */
  overallCall?: 'safe' | 'unsafe'
  /**
   * Whether the SERVER passed that call — `passed` from gradeInboxCall, which
   * computes it with the same scoreInboxCall the submit path uses. This is the
   * verdict. Absent when the gate was not graded, and on a session persisted
   * before the field existed.
   */
  gatePassed?: boolean
  /**
   * The BE round id. Lets a round POST that lands late find ITS entry, rather
   * than whichever round happens to be on screen when it arrives.
   */
  roundId?: string
  /**
   * Points the server actually CREDITED for this round, from the submit
   * response. Absent until that response lands, and forever when nothing was
   * credited to the learner's score (an impersonated preview sends no POST; a
   * failed POST credits nothing). A Library replay credits 0 and says so.
   */
  bankedPoints?: number
  elementAnswers?: Record<string, SafetyStatus>
}

/**
 * The round record the player keeps for a session — ONE declaration.
 *
 * PlayerShell and ResultsScreen each used to declare their own copy of this
 * shape, and this module a third near-identical one; nothing linked them, so a
 * field added to one silently went missing from another.
 */
export interface ChallengeAnswer extends SessionRoundEntry {
  emailId: number
  elementAnswers: Record<string, SafetyStatus>
}

export interface SessionAccuracy {
  totalCorrect: number
  totalElements: number
  /**
   * ELEMENT accuracy as a whole percent, 0 when no element was judged. Never
   * NaN. Neither screen headlines this any more — the session verdict is
   * judgementPercentage and the in-game header counts judgements too — so it is
   * the element figure and nothing else.
   */
  percentage: number
  /** How many rounds ended at the gate — shown instead of an element table. */
  gateOnlyCount: number
  /** Rounds that actually contributed to the element percentage. */
  scoredRounds: number
  /**
   * Inbox calls the SERVER graded this session — gate-only rounds AND hunt
   * rounds (scenarios B and C also begin with a call). Zero for Guided, which
   * never shows a gate, so the judgement figure below collapses to the element
   * figure with no mode flag needed.
   */
  callsMade: number
  /** Of those, how many the server passed. */
  callsCorrect: number
  /** Calls the server FAILED where the learner said Safe — a trusted phish. */
  trustedPhishCalls: number
  /** Calls the server FAILED where the learner said Unsafe — a false alarm. */
  falseAlarmCalls: number
  /**
   * Numerator of judgementPercentage: elements right, plus INBOX_CALL_WEIGHT
   * for every graded call that was right.
   */
  judgementsCorrect: number
  /**
   * Denominator of judgementPercentage: elements judged, plus INBOX_CALL_WEIGHT
   * for every graded call.
   */
  judgementsTotal: number
  /**
   * THE HEADLINE FIGURE: every decision the learner was actually asked to make,
   * weighted. One per element judged, INBOX_CALL_WEIGHT per graded inbox call.
   * NULL when they were asked for nothing — the ABSENCE of a score, which a
   * screen must not render as the score zero.
   *
   * It exists because element accuracy alone let a learner who trusted a phish
   * (scenario B, recovered on the hunt) or false-alarmed twice in a mixed
   * session read a clean "100% · Expert" (review, 2026-09-22). The weight — how
   * much one call counts against one flag — was decided on 2026-09-23: three.
   */
  judgementPercentage: number | null
}

/**
 * The server's verdict on a round's inbox call, or null if there was none.
 *
 * THE ONLY place this rule lives — ResultsScreen's rows and ChallengeResults
 * call it rather than re-deriving it, which is how the three copies came to
 * disagree before (review, 2026-09-23).
 *
 * Never re-derives the verdict from the call except for a gate-only round
 * persisted before `gatePassed` existed, where "Safe is correct" is exactly as
 * correct as it was then. A round with a call but no verdict — the 4xx path,
 * where the server declined to grade — returns null: counting it wrong would
 * invent a mistake the learner never made.
 */
export function gradedCallVerdict(
  entry: Pick<SessionRoundEntry, 'gateOnly' | 'overallCall' | 'gatePassed'>,
): boolean | null {
  if (typeof entry.gatePassed === 'boolean') return entry.gatePassed
  if (entry.gateOnly === true && entry.overallCall) return entry.overallCall === 'safe'
  return null
}

/**
 * True when a round judged no elements and therefore has no element accuracy.
 *
 * Checks the COUNT as well as the flag on purpose. The server stores a
 * gate-only round as 0/0, so a payload that reaches the client without the flag
 * still must not drag the percentage — the same defence the backend's
 * averageRoundAccuracyPercent applies from the other side.
 */
export function isUnscorableRound(entry: SessionRoundEntry): boolean {
  return entry.gateOnly === true || !(entry.totalCount > 0)
}

/** Tally the session, excluding rounds that judged no elements. */
export function summariseSessionAccuracy(
  entries: SessionRoundEntry[],
): SessionAccuracy {
  let totalCorrect = 0
  let totalElements = 0
  let gateOnlyCount = 0
  let scoredRounds = 0
  let callsMade = 0
  let callsCorrect = 0
  let trustedPhishCalls = 0
  let falseAlarmCalls = 0

  for (const entry of entries) {
    // Counted for EVERY round, before the element branch — a hunt round has an
    // inbox call too (scenarios B and C), and dropping it is what let trusting
    // a phish render as a perfect score.
    const verdict = gradedCallVerdict(entry)
    if (verdict !== null) {
      callsMade += 1
      if (verdict) callsCorrect += 1
      else if (entry.overallCall === 'safe') trustedPhishCalls += 1
      else falseAlarmCalls += 1
    }

    if (isUnscorableRound(entry)) {
      if (entry.gateOnly === true) gateOnlyCount += 1
      continue
    }
    totalCorrect += entry.correctCount
    totalElements += entry.totalCount
    scoredRounds += 1
  }

  const judgementsCorrect = totalCorrect + callsCorrect * INBOX_CALL_WEIGHT
  const judgementsTotal = totalElements + callsMade * INBOX_CALL_WEIGHT
  return {
    totalCorrect,
    totalElements,
    // Guard the divisor — a pack of nothing but legitimate emails leaves no
    // elements to average, and NaN% would cascade into the grade thresholds.
    percentage:
      totalElements > 0 ? Math.round((totalCorrect / totalElements) * 100) : 0,
    gateOnlyCount,
    scoredRounds,
    callsMade,
    callsCorrect,
    trustedPhishCalls,
    falseAlarmCalls,
    judgementsCorrect,
    judgementsTotal,
    judgementPercentage:
      judgementsTotal > 0
        ? Math.round((judgementsCorrect / judgementsTotal) * 100)
        : null,
  }
}

export type SessionGrade = 'Expert' | 'Proficient' | 'Learning' | 'Needs Practice'

/**
 * The session's grade, from its judgement percentage.
 *
 * The ladder is the one the results screen has always used (>= 90 Expert,
 * >= 70 Proficient, >= 50 Learning), with ONE rule on top (2026-09-23): a
 * session with ANY wrong call is never graded Expert — it is capped at
 * Proficient, and `capped` says so. Two false alarms used to read 90% ·
 * "Expert" with a trophy.
 *
 * `grade` is null when nothing was judged: the absence of a score has no
 * grade, and must not fall through the ladder into "Needs Practice".
 */
export function gradeSession(
  summary: Pick<SessionAccuracy, 'judgementPercentage' | 'callsMade' | 'callsCorrect'>,
): { grade: SessionGrade | null; capped: boolean } {
  const pct = summary.judgementPercentage
  if (pct === null) return { grade: null, capped: false }
  const ladder: SessionGrade =
    pct >= 90 ? 'Expert' : pct >= 70 ? 'Proficient' : pct >= 50 ? 'Learning' : 'Needs Practice'
  const anyWrongCall = summary.callsCorrect < summary.callsMade
  if (ladder === 'Expert' && anyWrongCall) return { grade: 'Proficient', capped: true }
  return { grade: ladder, capped: false }
}

/**
 * ONE round's judgement score as a whole percent — elements plus the graded
 * call at INBOX_CALL_WEIGHT — or null when nothing was judged. The server's
 * judgementPercent(roundJudgements(round)), mirrored.
 */
export function roundJudgementPercent(entry: SessionRoundEntry): number | null {
  const verdict = gradedCallVerdict(entry)
  const elements = isUnscorableRound(entry)
    ? { correct: 0, total: 0 }
    : { correct: entry.correctCount, total: entry.totalCount }
  const correct = elements.correct + (verdict === true ? INBOX_CALL_WEIGHT : 0)
  const total = elements.total + (verdict === null ? 0 : INBOX_CALL_WEIGHT)
  return total > 0 ? Math.round((correct / total) * 100) : null
}

/**
 * Whether ONE round passed — the pass/miss the per-round reveal shows.
 *
 * The web mirror of the server's roundOutcome: a WRONG call is a miss however
 * the hunt went (a trusted phish is not a pass), otherwise the round's weighted
 * judgements must reach 70%. A round with nothing judged has not passed.
 */
export function roundPassed(entry: SessionRoundEntry): boolean {
  if (gradedCallVerdict(entry) === false) return false
  const pct = roundJudgementPercent(entry)
  return pct !== null && pct >= 70
}
